import { CSS_SELECTOR, useCustomCss } from '../../contexts/CustomCss';
import { CheckMarkIcon, GlobeIcon } from '../../assets';
import { Fragment, h } from 'preact';
import { GlobeContainer, Menu, MenuContainer, MenuItem, TextContainer } from './Selector.styles';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import ReactTooltip from 'react-tooltip';
import { TOOLTIP_ID } from '../../constants';
import { useGlobalStateContext } from '../../contexts/GlobalContext';
import { useKeyPress } from '../../hooks';

interface Props<T> {
  onChange: (selected: T) => void;
  items: SelectItem<T>[];
  ariaLabel?: string;
  defaultItem?: SelectItem<T>;
  id?: string;
  isExpandedOnInit?: boolean;
  maxHeight?: number;
  testIdContainer?: string;
  testIdMenu?: string;
  testIdMenuButton?: string;
}

type SelectItem<T> = {
  ariaLabel: string;
  displayName: string;
  value: T;
};

interface SelectorTooltipElement extends HTMLDivElement {
  base: HTMLDivElement;
}

function Selector<T>({
  onChange,
  items,
  ariaLabel,
  defaultItem,
  id,
  isExpandedOnInit,
  maxHeight,
  testIdContainer,
  testIdMenu,
  testIdMenuButton,
}: Props<T>): h.JSX.Element {
  const [dropdownExpanded, setDropdownExpanded] = useState(isExpandedOnInit);
  const [selectedItem, setSelected] = useState(defaultItem || items[0]);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(window.devicePixelRatio * 100);
  const isUpArrowKeyPressed = useKeyPress({ targetKey: 'ArrowUp' });
  const isDownArrowKeyPressed = useKeyPress({ targetKey: 'ArrowDown' });
  const isEscapePressed = useKeyPress({ targetKey: 'Escape' });
  const [tooltipLeft, setTooltipLeft] = useState(0);
  const [tooltipTop, setTooltipTop] = useState(0);
  const isEnterPressed = useKeyPress({ targetKey: 'Enter' });

  const { rules } = useCustomCss(CSS_SELECTOR.LANGUAGE_SWITCH);
  const selectedMenuItem = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const globeIcon = useRef<HTMLButtonElement>(null);

  const { setBlockFocus, setNextFocus, setLangSelectorStatus } = useGlobalStateContext();

  const clickListener = useCallback(
    (event: MouseEvent) => {
      // eslint-disable-next-line
      const clickPath = ((event as any).path as EventTarget[]) || (event.composedPath && event.composedPath());

      if (clickPath) {
        // eslint-disable-next-line
        const isPartOfLanguageSelector = clickPath.some((elem) => (elem as any).id === id);

        if (!isPartOfLanguageSelector) {
          setDropdownExpanded(false);
        }
      }
    },
    [id],
  );

  const updateGlobeDescribedBy = useCallback(() => {
    if (globeIcon && globeIcon.current) {
      globeIcon.current.setAttribute('aria-describedby', TOOLTIP_ID);
    }
  }, [globeIcon]);

  useEffect(() => {
    updateGlobeDescribedBy();
  });

  useEffect(() => {
    if (zoomLevel >= 300 && tooltipRef && tooltipRef.current) {
      (tooltipRef.current as SelectorTooltipElement).base.style.maxWidth = '60%';
    } else if (tooltipRef && tooltipRef.current) {
      (tooltipRef.current as SelectorTooltipElement).base.style.removeProperty('max-width');
    }
  }, [tooltipVisible, zoomLevel]);

  useEffect(() => {
    window.addEventListener('resize', () => {
      const browserZoomLevel = Math.round(window.devicePixelRatio * 100);
      setZoomLevel(browserZoomLevel);
    });
  }, []);

  useEffect(() => {
    if (isEscapePressed && dropdownExpanded) {
      setDropdownExpanded(!dropdownExpanded);
      setNextFocus(globeIcon);
    }

    if (isEscapePressed && !dropdownExpanded && tooltipVisible) {
      ReactTooltip.hide();
      setNextFocus(globeIcon);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEscapePressed]);

  useEffect(() => {
    if (dropdownExpanded && isDownArrowKeyPressed) {
      if (!document.activeElement) {
        selectedMenuItem.current?.focus();
      } else {
        const activeElement = document.activeElement?.shadowRoot?.querySelector(':focus-visible');

        if (activeElement?.getAttribute('role') === 'option' && activeElement.nextSibling) {
          (activeElement.nextSibling as HTMLElement).focus();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDownArrowKeyPressed]);

  useEffect(() => {
    if (dropdownExpanded && isUpArrowKeyPressed) {
      if (!document.activeElement) {
        selectedMenuItem.current?.focus();
      } else {
        const activeElement = document.activeElement?.shadowRoot?.querySelector(':focus-visible');

        if (activeElement?.getAttribute('role') === 'option' && activeElement.previousSibling) {
          (activeElement.previousSibling as HTMLElement).focus();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpArrowKeyPressed]);

  useEffect(() => {
    document.addEventListener('click', clickListener);

    return (): void => {
      document.removeEventListener('click', clickListener);
    };
  }, [clickListener]);

  useEffect(() => {
    if (dropdownExpanded && selectedMenuItem) {
      setBlockFocus(true);
      selectedMenuItem.current?.focus();
    }
    setLangSelectorStatus(dropdownExpanded || false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownExpanded]);

  useEffect(() => {
    setSelected(defaultItem || items[0]);
  }, [defaultItem, items]);

  const onClickHandler = useCallback(
    (selectItem: SelectItem<T>, withNextFocus: boolean): void => {
      setSelected(selectItem);
      onChange(selectItem.value);

      if (withNextFocus) {
        setNextFocus(globeIcon);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange],
  );

  useEffect(() => {
    if (tooltipRef && tooltipRef.current) {
      (tooltipRef.current as SelectorTooltipElement).base.addEventListener('mouseenter', () => {
        updateGlobeDescribedBy();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showTooltip = useCallback(() => {
    if (tooltipRef?.current != null) {
      ReactTooltip.show(tooltipRef.current);
    }
  }, [tooltipRef]);

  const hideTooltip = useCallback(() => {
    if (tooltipRef?.current != null) {
      ReactTooltip.hide(tooltipRef.current);
    }
  }, [tooltipRef]);

  const setTooltipLanguageStatus = useCallback(
    (status: boolean) => {
      setLangSelectorStatus(status);
      setTooltipVisible(status);
    },
    [setLangSelectorStatus],
  );

  return (
    <MenuContainer data-testid={testIdContainer} dropdownExpanded={dropdownExpanded} id={id}>
      {dropdownExpanded ? (
        <Menu role="listbox" data-testid={testIdMenu} maxHeight={maxHeight}>
          <MenuItem
            aria-label={selectedItem.displayName}
            aria-posinset={1}
            aria-selected
            aria-setsize={items && items.length}
            data-testid={`uc-menu-item-${selectedItem.value}`}
            lang={`${selectedItem.value}`}
            onClick={(): void => {
              setDropdownExpanded(!dropdownExpanded);

              if (isEnterPressed) {
                setNextFocus(globeIcon);
              }
            }}
            role="option"
            ref={selectedMenuItem}
            style={{ ...rules }}
          >
            <TextContainer>{selectedItem.displayName}</TextContainer>
            <CheckMarkIcon />
          </MenuItem>
          {items
            .filter((item) => item.value !== selectedItem.value)
            .map((item, index) => {
              return (
                <MenuItem
                  aria-label={item.displayName}
                  aria-posinset={index + 2}
                  aria-setsize={items && items.length}
                  data-testid={`uc-menu-item-${item.value}`}
                  lang={`${item.value}`}
                  onClick={(): void => {
                    setDropdownExpanded(!dropdownExpanded);
                    onClickHandler(item, isEnterPressed);
                  }}
                  role="option"
                  style={{ ...rules }}
                >
                  {item.displayName}
                </MenuItem>
              );
            })}
        </Menu>
      ) : (
        <Fragment>
          <GlobeContainer
            aria-label={ariaLabel}
            data-tip
            data-for={TOOLTIP_ID}
            aria-haspopup="listbox"
            aria-describedby={TOOLTIP_ID}
            data-testid={testIdMenuButton}
            onFocus={showTooltip}
            onBlur={hideTooltip}
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
            onClick={() => {
              hideTooltip();
              setDropdownExpanded(true);
            }}
            ref={globeIcon}
          >
            <GlobeIcon label={ariaLabel} />
          </GlobeContainer>
        </Fragment>
      )}
      {!dropdownExpanded && (
        <ReactTooltip
          arrowColor="transparent"
          ref={tooltipRef}
          clickable
          // TODO: This `disable` prop must be removed when we add the
          // tooltip text translations
          disable
          id={TOOLTIP_ID}
          effect="solid"
          place="left"
          role="tooltip"
          uuid={TOOLTIP_ID}
          delayShow={100}
          overridePosition={({ left, top }) => {
            if (tooltipLeft === 0 && tooltipTop === 0) {
              setTooltipLeft(left);
              setTooltipTop(top);
            }

            return { left: tooltipLeft, top: tooltipTop };
          }}
          afterShow={() => setTooltipLanguageStatus(true)}
          afterHide={() => setTooltipLanguageStatus(false)}
        >
          <div onMouseEnter={updateGlobeDescribedBy} onFocus={updateGlobeDescribedBy} lang="en">
            Selecting an option will immediately change the language
          </div>
        </ReactTooltip>
      )}
    </MenuContainer>
  );
}

Selector.defaultProps = {
  ariaLabel: null,
  defaultItem: null,
  id: '',
  isExpandedOnInit: false,
  maxHeight: 100,
  testIdContainer: '',
  testIdMenu: '',
  testIdMenuButton: '',
};
export default Selector;
